import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';

const isValidInternationalPhone = (value: string) => {
  const normalized = (value ?? '').trim();

  if (!normalized) return false;
  if (!/^\+[1-9]\d{1,14}$/.test(normalized)) return false;

  return isValidPhoneNumber(normalized);
};

export const loginSchema = z.object({
  username: z.string().nonempty('Username is required'),
  password: z.string().nonempty('Password is required'),
});

// Register

export const registerSchema = z.object({
  username: z
    .string()
    .min(4, 'Username must be 4 to 15 characters')
    .max(15, 'Username must be 4 to 15 characters')
    .regex(/^[a-zA-Z0-9]+$/, 'Username must be alphanumeric'),
  mobile: z
    .string({
      required_error: 'Mobile number is required',
      invalid_type_error: 'Mobile number is required',
    })
    .trim()
    .nonempty('Mobile number is required')
    .refine(
      (value) => isValidInternationalPhone(value),
      'Enter a valid phone number with country code'
    ),
  password: z
    .string()
    .min(6, 'Password must be 6 to 20 characters')
    .max(20, 'Password must be 6 to 20 characters')
    .regex(
      /^[a-zA-Z0-9@._#$!%&*-]+$/,
      'Only letters (A-Z, a-z), numbers (0-9), and these special characters are allowed: @ . _ # $ ! % & * -'
    ),
  referralCode: z.string().optional(),
});

// Forgot Password

export const phoneSchema = z.object({
  mobile: z
    .string()
    .min(1, 'Mobile number is required')
    .max(20, 'Mobile number must be at most 20 digits')
    .regex(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number'),
});

export const resetPasswordSchema = z
  .object({
    otp: z.string().min(1, 'Verification code is required').min(6, 'OTP must be at least 6 digits'),
    newPassword: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

// Add Account

export const accountSchema = z.object({
  accountType: z.string().min(1, 'Account type is required'),
  bankName: z
    .string()
    .trim()
    .min(3, 'Bank name must be at least 3 characters')
    .max(50, 'Bank name must be at most 50 characters'),

  holderName: z
    .string()
    .trim()
    .min(3, 'Holder name must be at least 3 characters')
    .max(50, 'Holder name must be at most 50 characters'),
  accountNumber: z
    .string()
    .trim()
    .min(9, 'Account number must be at least 9 digits')
    .regex(/^\d+$/, 'Only numbers allowed'),
  ifscCode: z.string().min(6, 'Must be at least 6 characters'),
});

export const upiSchema = z.object({
  name: z.string().trim().min(3, 'Name must be at least 3 characters'),
  upiId: z
    .string()
    .trim()
    .regex(/^[\w.-]+@[\w.-]+$/, 'Invalid UPI ID'),
  number: z
    .string()
    .trim()
    .max(20, 'Phone number (including +) must be at most 20 characters')
    .regex(/^\+?\d+$/, 'Phone number may start with + and must contain only digits'),
});

export const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),

    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(14, 'Password must be at most 14 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),

    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

// Deposit and Withdrawal Schemas

export const depositSchema = z.object({
  txnRef: z
    .string()
    .trim()
    .min(3, 'Transaction reference is required')
    .max(50, 'Transaction reference is too long'),

  amount: z.coerce
    .number()
    .int('Amount must be a whole number')
    .positive('Amount must be greater than zero'),

  proof: z
    .custom<File>((file) => file instanceof File, 'File is required')
    .refine((file) => file.size <= 5 * 1024 * 1024, 'Maximum file size is 5 MB')
    .refine(
      (file) => ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type),
      'Upload a JPG, PNG, WebP, or PDF file'
    ),

  agree: z.boolean().refine((value) => value, 'You must accept the payment and withdrawal policy'),
});

export type DepositFormValues = z.infer<typeof depositSchema>;

export const accountSchemaForWithdraw = z.object({
  accountType: z.enum(['savings', 'current'], {
    errorMap: () => ({ message: 'Select account type' }),
  }),
  bankName: z.string().trim().min(2, 'Bank name is required').max(50, 'Bank name is too long'),

  accountHolderName: z
    .string()
    .trim()
    .min(2, 'Account holder name is required')
    .max(50, 'Account holder name is too long'),

  accountNumber: z
    .string()
    .min(8, 'Account number too short')
    .max(18, 'Account number too long')
    .regex(/^\d+$/, 'Account number must be numeric'),

  ifsc: z
    .string()
    .trim()
    .length(11, 'IFSC must be 11 characters')
    .regex(/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/, 'Invalid IFSC code')
    .transform((value) => value.toUpperCase()),
});

export type AccountFormValues = z.infer<typeof accountSchemaForWithdraw>;

export const upiSchemaForWithdraw = z.object({
  name: z.string().trim().min(2, 'Name is required').max(50, 'Name is too long'),

  upiId: z
    .string()
    .trim()
    .min(3, 'UPI is required')
    .regex(/^[\w.-]+@[\w.-]+$/, 'Invalid UPI ID'),

  phone: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number'),
});

export type UpiFormValues = z.infer<typeof upiSchemaForWithdraw>;
